import React from 'react';
import { Badge, Stack } from '@chakra-ui/react';
import useT from '@/lib/t';
import { BuildingStateTag, ListingState, ListingStatusEnum, StateOfListing } from '@/swagger';
import { format } from 'date-fns';

type Props = {
  state: Array<ReplaceValueType<ListingState, Date, number>>;
  status: ListingStatusEnum | null;
  sold_at: string | null;
};

const ColorScheme: Record<StateOfListing, [string, string, ({ outline?: string; opacity?: number } | undefined)?]> = {
  // [color, bg, additionalChakraStyleProps]
  [StateOfListing.EXCLUSIVE]: ['gold', 'white', { outline: '1px solid #b49961' }],
  [StateOfListing.DISCOUNT]: ['white', 'black'],
  [StateOfListing.NONE]: ['white', 'black'],
  [StateOfListing.NEW]: ['white', 'black', { opacity: 0.64 }],
  [StateOfListing.OPEN_HOUSE]: ['white', 'black', { opacity: 0.64 }],
  [StateOfListing.MODEL_HOME]: ['white', 'black', { opacity: 0.64 }],
  [StateOfListing.SOLD]: ['white', 'brand', { outline: 'brand' }],
  [StateOfListing.PENDING]: ['white', 'brand', { outline: 'brand' }],
};

type StateItem = Pick<ListingState, 'state' | 'label'> & { expire_at: string | number | null };

const isNotExpired = ({ expire_at }: Pick<StateItem, 'expire_at'>) =>
  !expire_at || new Date(expire_at).getTime() >= Date.now();

export const getActiveModelHome = (state: StateItem[] = []) =>
  state.find((item) => item.state === StateOfListing.MODEL_HOME && isNotExpired(item));

const ModelHomeBadgeItem = ({ label }: { label: string | null }) => (
  <Badge
    borderRadius={0}
    fontWeight='regular'
    fontSize='xs'
    color={ColorScheme[StateOfListing.MODEL_HOME][0]}
    px={2}
    py={0.5}
    ml={0}
    gap={3}
    bg={ColorScheme[StateOfListing.MODEL_HOME][1]}
    {...(ColorScheme[StateOfListing.MODEL_HOME][2] || {})}
    style={{ columnGap: 12, rowGap: 12, WebkitMarginStart: 0 }}
  >
    {label || 'Model Home'}
  </Badge>
);

// for listing cards that show only the Model Home badge (parent must be `relative`)
export function ModelHomeBadge({ state }: { state?: StateItem[] | null }) {
  const modelHome = getActiveModelHome(state ?? []);
  if (!modelHome) return null;

  return (
    <div className='absolute z-20 top-0 left-0 leading-4'>
      <Stack direction='row' mt={3} ml={3}>
        <ModelHomeBadgeItem label={modelHome.label} />
      </Stack>
    </div>
  );
}

export const isBuildingSoldOut = (state: Array<{ state: BuildingStateTag }> | null = []) =>
  (state ?? []).some((item) => item.state === BuildingStateTag.SOLD_OUT);

// for building cards / detail gallery, styled like the listing sold badge (parent must be `relative`)
export function BuildingSoldOutBadge() {
  return (
    <div className='absolute z-20 top-0 left-0 leading-4'>
      <Stack direction='row' mt={3} ml={3}>
        <Badge
          borderRadius={0}
          fontWeight='regular'
          fontSize='xs'
          color={ColorScheme[StateOfListing.SOLD][0]}
          px={2}
          py={0.5}
          ml={0}
          bg={ColorScheme[StateOfListing.SOLD][1]}
          style={{ WebkitMarginStart: 0 }}
        >
          完銷
        </Badge>
      </Stack>
    </div>
  );
}

export default function BadgeContainer({ state = [], sold_at }: Props) {
  const c = useT('share');
  const isSoldOut = state.some(({ state }) => state === StateOfListing.SOLD);
  const modelHome = getActiveModelHome(state);

  const badges =
    // show only the sold badge (plus Model Home) if it's sold
    isSoldOut ? (
      <>
        <div>
          <Badge
            borderRadius={0}
            fontWeight='regular'
            fontSize='xs'
            color={ColorScheme[StateOfListing.SOLD][0]}
            px={2}
            py={0.5}
            ml={0}
            gap={3}
            bg={ColorScheme[StateOfListing.SOLD][1]}
            style={{ columnGap: 12, rowGap: 12, WebkitMarginStart: 0 }}
          >
            {c('sold')}
          </Badge>
          <Badge
            borderRadius={0}
            fontWeight='regular'
            fontSize='xs'
            color={'brand'}
            px={2}
            py={0.5}
            ml={0}
            gap={3}
            bg={'white'}
            style={{ columnGap: 12, rowGap: 12, WebkitMarginStart: 0 }}
          >
            {sold_at && format(new Date(sold_at), 'yyyy/MM/dd')}
          </Badge>
        </div>
        {modelHome && <ModelHomeBadgeItem label={modelHome.label} />}
      </>
    ) : (
      state
        .filter(({ expire_at }) => !expire_at || new Date(expire_at).getTime() >= Date.now())
        .map(({ state, label }) => {
          if (state === StateOfListing.NONE) return null;
          if (state === StateOfListing.MODEL_HOME) return <ModelHomeBadgeItem key={state} label={label} />;
          return (
            <Badge
              key={state}
              borderRadius={0}
              fontWeight='regular'
              fontSize='xs'
              color={ColorScheme[state][0]}
              px={2}
              py={0.5}
              ml={0}
              gap={3}
              bg={ColorScheme[state][1]}
              {...(ColorScheme[state][2] || {})}
              style={{ columnGap: 12, rowGap: 12, WebkitMarginStart: 0 }}
            >
              {label}
            </Badge>
          );
        })
    );

  return (
    <div className='absolute z-20 w-full top-0 left-0 leading-4'>
      <Stack direction='row' wrap='wrap' className='w-4/5' style={{ rowGap: '12px', columnGap: '12px' }} mt={3} ml={3}>
        {badges}
      </Stack>
    </div>
  );
}
